import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Smartphone, IndianRupee, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable UPI QR-code payment modal.
 * Props:
 *  - open, onClose
 *  - order: { upi_id, upi_link, upi_merchant_name, amount (paise), order_id }
 *  - lang: "hi" | "en"
 *  - description: string
 */
export function UpiQrPayModal({ open, onClose, order, lang = "hi", description }) {
  if (!open || !order) return null;
  const amountRupees = Math.round(order.amount / 100);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => {});
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-md w-full card-shadow overflow-hidden" data-testid="upi-qr-modal">
        <div className="p-5 border-b bg-gradient-to-br from-blue-600 to-emerald-500 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading text-lg font-semibold">
                {lang==="hi"?"UPI से भुगतान करें":"Pay with UPI"}
              </h3>
              <p className="text-xs opacity-90 mt-0.5">{description || (lang==="hi"?"किसी भी UPI ऐप से scan करें":"Scan with any UPI app")}</p>
            </div>
            <div className="text-right">
              <div className="text-[10px] opacity-80 uppercase tracking-wider">{lang==="hi"?"राशि":"Amount"}</div>
              <div className="font-heading text-3xl font-bold flex items-center gap-0.5">
                <IndianRupee size={22}/>{amountRupees}
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 flex flex-col items-center">
          <div className="p-4 bg-white rounded-2xl border-2 border-slate-100 relative">
            <QRCodeSVG
              value={order.upi_link}
              size={220}
              level="M"
              bgColor="#FFFFFF"
              fgColor="#0F172A"
              includeMargin={false}
            />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-11 h-11 rounded-full bg-white shadow-md flex items-center justify-center">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-emerald-500 flex items-center justify-center text-white font-heading font-bold text-xs">
                  AO
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 text-center">
            <div className="font-heading font-semibold">{order.upi_merchant_name || "Kaushal Kumar"}</div>
            <button
              onClick={() => copy(order.upi_id, "UPI ID")}
              className="text-sm text-slate-600 hover:text-blue-600 inline-flex items-center gap-1 mt-1"
              data-testid="upi-copy-id"
            >
              <span className="font-mono">{order.upi_id}</span>
              <Copy size={12}/>
            </button>
          </div>

          <a
            href={order.upi_link}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white py-3 hover:bg-slate-800 active:scale-95"
            data-testid="upi-open-app"
          >
            <Smartphone size={18}/> {lang==="hi"?"UPI ऐप में खोलें":"Open in UPI app"}
          </a>

          {order.razorpay_payment_link && (
            <a
              href={order.razorpay_payment_link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 hover:opacity-95 active:scale-95"
              data-testid="razorpay-payment-link"
            >
              <ExternalLink size={16}/>
              {lang==="hi"
                ? `Razorpay से ₹${amountRupees} pay करें (KAUSHAL KUMAR)`
                : `Pay ₹${amountRupees} via Razorpay (KAUSHAL KUMAR)`}
            </a>
          )}

          <div className="mt-5 w-full rounded-2xl bg-slate-50 p-3 text-xs text-slate-600 leading-relaxed">
            <div className="font-medium text-slate-800 mb-1">
              {lang==="hi"?"कैसे pay करें:":"How to pay:"}
            </div>
            1. {lang==="hi"?"अपना कोई भी UPI ऐप खोलें (GPay/PhonePe/Paytm)":"Open any UPI app (GPay / PhonePe / Paytm)"}<br/>
            2. {lang==="hi"?`QR code scan करें और ₹${amountRupees} pay करें`:`Scan the QR and pay ₹${amountRupees}`}<br/>
            3. {lang==="hi"?"Payment के बाद हमें SMS/Email से confirmation मिल जाएगी":"You'll receive an SMS/Email once payment is confirmed"}
          </div>

          <div className="mt-5 w-full">
            <button onClick={onClose} className="btn-outline w-full" data-testid="upi-cancel">
              {lang==="hi"?"बंद करें":"Close"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
